import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import api from "../../services/api";
import Modal from "../../components/ui/Modal";
import { FormError, ModalFoot } from "../../components/ui/panelKit";
import { readApiError } from "../../utils/apiError";

const LABELS = {
  external_object_id: "Obyekt identifikatori",
  title: "Sarlavha",
  object_url: "Havola",
};

/**
 * Starting a discussion for an object by hand (FR-014).
 *
 * Normally the client system opens the discussion itself, through the API,
 * when somebody opens the object's card. This is for when the object exists
 * but that has not happened -- the integration is not deployed yet, or the
 * object predates it.
 *
 * The object id is the part worth getting right: it is how the widget on the
 * object's page finds this discussion. Typed wrongly, the thread exists but
 * nothing ever shows it. An object that already has one answers 409 with the
 * existing thread, which is offered rather than reported as an error.
 */
const NewThreadModal = ({ open, clientId, onClose }) => {
  const navigate = useNavigate();
  const [objectId, setObjectId] = useState("");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [existing, setExisting] = useState(null);

  const close = () => {
    setObjectId("");
    setTitle("");
    setUrl("");
    setFormError("");
    setExisting(null);
    onClose();
  };

  const save = async () => {
    setSaving(true);
    setFormError("");
    setExisting(null);
    try {
      const res = await api.post("/threads/", {
        client: clientId,
        external_object_id: objectId.trim(),
        title: title.trim(),
        object_url: url.trim(),
      });
      close();
      navigate(`/muhokamalar/${res.data.id}`);
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.thread) {
        setExisting(err.response.data.thread);
      } else {
        setFormError(readApiError(err, LABELS, "Muhokamani yaratib boʻlmadi."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Yangi muhokama"
      onClose={close}
      footer={
        <ModalFoot
          onCancel={close}
          onSave={save}
          saving={saving}
          saveLabel="Yaratish"
          savingLabel="Yaratilmoqda..."
          disabled={!objectId.trim() || !title.trim() || !url.trim()}
        />
      }
    >
      <div className="ms-form-grid ms-form-grid--stack">
        <div className="ms-field ms-field--full">
          <label htmlFor="ms-thread-object">
            Obyekt identifikatori <span>*</span>
          </label>
          <input
            id="ms-thread-object"
            type="text"
            className="ms-mono"
            placeholder="eqaror:document:47354"
            value={objectId}
            autoFocus
            onChange={(e) => setObjectId(e.target.value)}
          />
          <span className="ms-field-hint">
            Obyektning tizimingizdagi identifikatori — widget muhokamani aynan shu
            qiymat boʻyicha topadi. Integratsiya yuboradigan qiymat bilan bir xil
            boʻlishi shart.
          </span>
        </div>

        <div className="ms-field ms-field--full">
          <label htmlFor="ms-thread-title">
            Sarlavha <span>*</span>
          </label>
          <input
            id="ms-thread-title"
            type="text"
            placeholder="Hujjat nomi"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="ms-field ms-field--full">
          <label htmlFor="ms-thread-url">
            Havola <span>*</span>
          </label>
          <input
            id="ms-thread-url"
            type="text"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <span className="ms-field-hint">
            Obyekt tizimingizda ochiladigan manzil — mobil ilova va kabinet undan
            obyektga qaytish uchun foydalanadi.
          </span>
        </div>

        {existing && (
          <div className="ms-note" style={{ marginTop: 0 }}>
            <span>Bu obyektning muhokamasi allaqachon bor</span>
            <p>
              <Link to={`/muhokamalar/${existing.id}`} onClick={close}>
                «{existing.title}» ni ochish
              </Link>
            </p>
            {existing.link && (
              <p className="ms-mono" style={{ marginTop: 6, wordBreak: "break-all" }}>
                {existing.link}
              </p>
            )}
          </div>
        )}
        <FormError>{formError}</FormError>
      </div>
    </Modal>
  );
};

export default NewThreadModal;
